import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

/**
 * CollectionsManager
 * ------------------
 * Admin panel section for editing the four collection pages — title, eyebrow
 * tagline, curator description, and an optional "show only N latest products"
 * cap. Mounted inside Admin.jsx beneath the product table.
 */
export default function CollectionsManager() {
  const [list, setList] = useState([]);
  const [savingSlug, setSavingSlug] = useState(null);

  const load = () =>
    api.get("/categories").then(({ data }) => setList(data));

  useEffect(() => { load(); }, []);

  const updateField = (slug, field, value) => {
    setList((prev) =>
      prev.map((c) => (c.slug === slug ? { ...c, [field]: value } : c)),
    );
  };

  const save = async (cat) => {
    setSavingSlug(cat.slug);
    try {
      await api.put(`/categories/${cat.slug}`, {
        title: (cat.title || "").trim(),
        eyebrow: (cat.eyebrow || "").trim(),
        description: (cat.description || "").trim(),
        max_products: cat.max_products === "" || cat.max_products == null
          ? null
          : Math.max(1, Number(cat.max_products)),
      });
      toast.success(`${cat.title} collection updated`);
      load();
    } catch {
      toast.error("Could not save");
    } finally {
      setSavingSlug(null);
    }
  };

  return (
    <section className="mt-20" data-testid="collections-manager">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-serif-h font-light text-3xl text-stone-900">Collections</h2>
        <div className="text-xs tracking-overline uppercase text-stone-500">Edit the title, tagline & curator note for each shop page</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {list.map((cat) => (
          <div
            key={cat.slug}
            className="border border-warm bg-white p-6 rounded-sm"
            data-testid={`cat-card-${cat.slug}`}
          >
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-[11px] tracking-overline uppercase text-[#D4AF37]">
                /shop/{cat.slug}
              </div>
              {cat.updated_at && (
                <div className="text-[10px] text-stone-400">
                  Updated {new Date(cat.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </div>
              )}
            </div>

            <label className="text-[11px] tracking-overline uppercase text-stone-500">Title</label>
            <input
              className="input-line"
              value={cat.title}
              onChange={(e) => updateField(cat.slug, "title", e.target.value)}
              data-testid={`cat-title-${cat.slug}`}
            />

            <label className="text-[11px] tracking-overline uppercase text-stone-500 mt-4 block">Featured eyebrow</label>
            <input
              className="input-line"
              placeholder="e.g. The Saree of the Moment"
              value={cat.eyebrow}
              onChange={(e) => updateField(cat.slug, "eyebrow", e.target.value)}
              data-testid={`cat-eyebrow-${cat.slug}`}
            />

            <label className="text-[11px] tracking-overline uppercase text-stone-500 mt-4 block">Curator description</label>
            <textarea
              className="input-line min-h-[88px]"
              placeholder="A short paragraph that appears on the collection hero…"
              value={cat.description}
              onChange={(e) => updateField(cat.slug, "description", e.target.value)}
              data-testid={`cat-desc-${cat.slug}`}
            />

            <label className="text-[11px] tracking-overline uppercase text-stone-500 mt-4 block">
              Show only latest N products <span className="normal-case tracking-normal text-stone-400">(blank = show all)</span>
            </label>
            <input
              type="number"
              min="1"
              className="input-line"
              placeholder="e.g. 8"
              value={cat.max_products ?? ""}
              onChange={(e) => updateField(cat.slug, "max_products", e.target.value)}
              data-testid={`cat-max-${cat.slug}`}
            />

            <button
              type="button"
              onClick={() => save(cat)}
              disabled={savingSlug === cat.slug}
              className="btn-ink w-full justify-center mt-6 disabled:opacity-50"
              data-testid={`cat-save-${cat.slug}`}
            >
              <Save size={14} className="mr-2" />
              {savingSlug === cat.slug ? "Saving…" : "Save Collection"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
